import {DataSplitInfo, PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {Page} from "../../../../misc/pdf/parts/Page";
import {PDFHeader} from "../../../../misc/pdf/parts/PDFHeader";
import {FrameAndDoorDetail} from "./FrameDetails";
import {orderByAscending, selectMany} from "nate-react-api-helpers";
import React, {useMemo} from "react";
import {PurchaseOrderItem} from "../../../../api/Logistics";
import {prepsGetCategorySorted} from "../../../project/shopdrawing/extras/preps/PrepUtil";
import {HardwareDetailItem} from "./HardwarePrepTable";
import {genericDistanceParser, isMetric} from "../../../../misc/scroller/Table";
import {ImperialDistance} from "../../../../misc/ImperialDistance";
import {MetricDistance} from "../../../../misc/MetricDistance";
import {getSpecialtyHingePrepLocation, removeActiveInActiveSuffix} from "../../warehouse/InventoryFrameTable";
import {css} from "@emotion/css";
import clsx from "clsx";
import {grey} from "@mui/material/colors";

export function DoorDetails(props: {
    value: FrameAndDoorDetail[]
    onReady(tf: boolean): void;
}) {
    if(props.value.length === 0) {
        setTimeout(() => props.onReady(true), 100);
        return null;
    }

    return (
        <PageRenderProvider key="door-details" data={props.value} onReady={props.onReady}>
            {(info: DataSplitInfo<FrameAndDoorDetail>) =>
                <Page orientation="landscape" key={info.pageIndex.toString()}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch"}}>
                        <PDFHeader noAddress title="Door Detail" />
                        <RenderTable<FrameAndDoorDetail> config={tableCfg} pageIndex={info.pageIndex} rows={info.rows} />
                    </div>
                </Page>
            }
        </PageRenderProvider>
    )
}

export function RenderTable<T>(props: {
    config: TblCfg<T>[];
    pageIndex: number;
    rows: T[];
}) {
    const rowCount = props.config.reduce((acc, item) => (item.row || 0) > acc ? (item.row || 0) : acc, 0) + 1;
    const rowConfigs: TblCfg<T>[][] = [];

    for(let i = 0; i < rowCount; i++) {
        rowConfigs.push(props.config.filter(r => (r.row || 0) === i));
    }

    return (
        <table className={table2}>
            <tbody>
            <tr key="head" className="header">
                {props.config.filter(r => (r.row || 0) === 0).map((r, index) => <th key={r.name + "-" + index.toString()}>{r.name}</th>)}
            </tr>
            {props.rows.map((r, index) => {

                const nonBlankRows = rowConfigs.filter((rowCfg, index) => {
                    const hasNonBlankCell = rowCfg
                        .filter(col => col.show ? col.show(r, props.rows) : true)
                        .map(col => assertIsArray(col.selector(r, "html") || []))
                        .find(v => !!v.find(s => !!s));

                    return !!hasNonBlankCell;
                });

                return (
                    <React.Fragment key={props.pageIndex + "-" + index.toString()}>
                        {nonBlankRows.map((rowCfg, index) => <tr key={index.toString()} className={clsx({
                            "first-section-row": index === 0,
                            "last-section-row": index === nonBlankRows.length - 1,
                        })}>
                            {rowCfg.map((col, index) => {
                                return (
                                    <React.Fragment key={index}>
                                        {col.indent ? <td colSpan={col.indent} /> : null}
                                        <td colSpan={col.colSpan}>
                                            {assertIsArray(col.selector(r, "html") || []).map(s => (
                                                <div>
                                                    {s}
                                                </div>
                                            ))}
                                        </td>
                                    </React.Fragment>
                                )
                            })}
                        </tr>)}
                    </React.Fragment>
                );
            })}
            </tbody>
        </table>
    )
}

export function tableCfgToCsv(tableCfg: TblCfg<FrameAndDoorDetail>[], items: FrameAndDoorDetail[]): string[][] {

    const rowCount = tableCfg.reduce((acc, item) => (item.row || 0) > acc ? (item.row || 0) : acc, 0);
    const rowConfigs: TblCfg<FrameAndDoorDetail>[][] = [];

    for(let i = 0; i < rowCount; i++) {
        rowConfigs.push(tableCfg.filter(r => (r.row || 0) === i));
    }

    let csvRows: string[][] = [];

    items.map((r, index) => {
        // filter for blank rows
        rowConfigs
            .filter((rowCfg, index) => {
                const hasNonBlankCell = rowCfg
                    .filter(col => col.show ? col.show(r, items) : true)
                    .map(col => assertIsArray(col.selector(r, "string") || []))
                    .find(v => !!v.find(s => !!s));

                return !!hasNonBlankCell;
            })
            .forEach(rowCfg => {
                let row: string[] = [];

                // setup header
                if(csvRows.length === 0) {
                    csvRows.push(tableCfg.map(col => col.name));
                }

                rowCfg.map((col, index) => {
                    if(col.indent) {
                        row.push(...Array(col.indent).fill(""));
                    }

                    row.push(col.selector(r, "string").join(", "));

                    if(col.colSpan) {
                        row.push(...Array(col.colSpan - 1).fill(""));
                    }
                });

                csvRows.push(row);
            });
    });

    return csvRows;
}

export const table2 = css({
    border: "1px solid transparent",
    borderCollapse: "collapse",
    fontSize: "0.8rem",
    minWidth: 300,

    "& td, & th": {
        paddingLeft: 2,
        paddingRight: 2,
        textAlign: "left",
    },

    "& .last-section-row td": {
        borderBottom: "1px dotted " + grey["600"],
        paddingBottom: 10,
    },

    "& .header th": {
        borderTop: "1px solid " + grey["800"]
    },

    "& tbody > tr:last-child td": {
        borderBottom: "1px solid " + grey["800"]
    }

})

export function assertIsArray<T>(value: T): T {
    if(typeof value !== "object" || !(value instanceof Array)) {
        console.error("value is not array!", value);
        return [] as any;
    }

    return value;
}

export type TblCfg<T> = {
    name: string
    selector: (r: T, format: "string" | "html") => any[];
    show?: (r: T, all: T[]) => boolean;
    indent?: number;
    row?: number
    colSpan?: number;
}

const tableCfg = [
    {name: "Opening", selector: (r) => [r.name]},
    {name: "Qty", selector: (r) => [r.qty.toString()]},
    {name: "Type", selector: (r) => [r.type]},
    {name: "Width", selector: (r) => [r.purchaseOrderItem.dimWidth]},
    {name: "Height", selector: (r) => [r.purchaseOrderItem.dimHeight]},
    {name: "Exact", selector: (r) => [r.doorExactHeight]},
    {name: "Handing", selector: (r) => [r.purchaseOrderItem.doorHanding]},
    {name: "Label", selector: (r) => [r.purchaseOrderItem.doorLabel]},
    {name: "Series", selector: (r) => [r.purchaseOrderItem.doorSeries]},
    {name: "Material", selector: (r) => [r.purchaseOrderItem.doorMaterial]},
    {name: "Gauge", selector: (r) => [r.purchaseOrderItem.doorGauge]},
    {name: "Thickness", selector: (r) => [r.purchaseOrderItem.doorThickness]},
    {name: "Door Elev", selector: (r) => [r.purchaseOrderItem.doorElevation]},
    {name: "Core", selector: (r) => [r.purchaseOrderItem.doorCore]},
    {name: "Seam", selector: (r) => [r.purchaseOrderItem.doorSeam]},
    {name: "Prep Elev", selector: (r) => [r.hardwarePrepDiagramName]},
    {name: "Hw Indent", row: 1, selector: r => [""]},
    {name: "Hardware Preps", row: 1, colSpan: 15, selector: (r, format) => makeHardwarePreps(r, "door", format)},
    {name: "Wd Indent", row: 2, selector: r => [""]},
    {name: "Wood Detail", row: 2, colSpan: 15, selector: (r, format) => makeWoodDetail(r, format)},
] as TblCfg<FrameAndDoorDetail>[];

function makeHtmlRow(list: any[], label?: any): any {
    return (
        <div style={{
            display: "flex",
            gap: 4,
        }}>{label}{list.map((l, index) =>
            <div style={{display: "flex"}}>{l}{index +1 !== list.length ? "," : ""}</div>
        )}</div>
    )
}

export function makeWoodDetail(r: FrameAndDoorDetail, format: "string" | "html") {
    let list: any[] = [];

    const sortedList: any[] = [];
    const raw: string[] = [];
    if(r.doorWoodDetail) {
        const w = r.doorWoodDetail;

        if(w.materialName) raw.push(w.materialName);
        if(w.cut) raw.push(w.cut);
        if(w.matching) raw.push(w.matching);
        if(w.matchingMethod) raw.push(w.matchingMethod);
        if(w.ply) raw.push(w.ply);
        if(w.finish) raw.push(w.finish);
        if(w.swatchName) raw.push(w.swatchName);
    }

    raw.forEach(v => {
        let name: any = v;
        if(format === "html") {
            name = <div>{name}</div>
        }

        sortedList.push(name);
    });

    if(sortedList.length > 0) {
        if(format === "html") {
            list.push(makeHtmlRow(sortedList))
        } else {
            list.push(sortedList.join(", "));
        }
    }

    return list;
}

export function makeHardwarePreps(r: FrameAndDoorDetail, kind: "door" | "frame", format: "string" | "html") {
    let list: any[] = [];

    if(kind === "door") {
        const sortedList: any[] = [];
        prepsGetCategorySorted(r.doorHardwarePreps).forEach(v => {
            if(v.value.type === "standard") {
                let name: any = v.key;
                if(format === "html") {
                    name = <div>{name}</div>
                }

                sortedList.push(name);
            } else if(v.value.type === "custom") {

                let name: any = v.value.productName || `<missing name:id=${v.value.product}>`;
                if(v.value.degreesOfOpening) {
                    name += ` @ ${v.value.degreesOfOpening}°`;
                }
                if(format === "html") {
                    name = <i>{name}</i>
                }

                sortedList.push(name);
            }
        });

        if(sortedList.length > 0) {
            if(format === "html") {
                list.push(makeHtmlRow(sortedList))
            } else {
                list.push(sortedList.join(", "));
            }
        }

        const locs = getSpecialtyHingePrepLocation(r.doorHardwarePreps);
        if (locs) {
            list.push(`Specialty Hinge Prep at ${locs.display}`);
        }

        if(list.length === 0) {
            return ["BLANK"];
        }
    }

    if(kind === "frame") {
        let unequal: string[] = [];
        const active: any[] = [];
        const inactive: any[] = [];
        const other: any[] = [];

        prepsGetCategorySorted(r.frameHardwarePreps).forEach(v => {
            if(v.value.type === "standard") {
                let name: any = removeActiveInActiveSuffix(v.key);
                if(format === "html") {
                    name = <span>{name}</span>
                }

                const frameSide = v.value.frameSide || "other";
                if(frameSide === "active") {
                    active.push(name);
                } else if(frameSide === "inactive") {
                    inactive.push(name);
                } else {
                    other.push(name);
                }

            } else if(v.value.type === "custom") {
                let name: any = v.value.productName || "<missing product id:" + v.value.product + ">";

                if(v.value.degreesOfOpening) {
                    name += ` @ ${v.value.degreesOfOpening}°`;
                }

                if(format === "html") {
                    name = <i>{name}</i>
                }

                const frameSide = v.value.frameSide || "other";
                if(frameSide === "active") {
                    active.push(name);
                } else if(frameSide === "inactive") {
                    inactive.push(name);
                } else {
                    other.push(name);
                }
            } else if(v.value.type === "unequalPairDoorSizes") {
                unequal.push("Unequal pair" + (v.value.slot ? ", slot " + v.value.slot : "") + " (active=" + v.value.activeDoorWidth + ", " + v.value.inactiveDoorWidth + ")");
            }
        });

        if(unequal.length > 0) {
            list.push(...unequal);
        }

        if(active.length > 0) {
            if(format === "html") {
                list.push(makeHtmlRow(active, <span>Active side:</span>));
            } else {
                list.push("Active side: " + active.join(", "))
            }

        }

        if(inactive.length > 0) {
            if(format === "html") {
                list.push(makeHtmlRow(inactive, <span>Inactive side:</span>));
            } else {
                list.push("Inactive side: " + inactive.join(", "))
            }
        }

        if(other.length > 0) {
            if(format === "html") {
                list.push(makeHtmlRow(other))
            } else {
                list.push(other.join(", "))
            }
        }

        const locs = getSpecialtyHingePrepLocation(r.frameHardwarePreps);
        if (locs) {
            list.push(`Specialty Hinge Prep at ${locs.display}`);
        }

        if(list.length === 0) {
            return ["BLANK"];
        }
    }

    return list;
}

export function doorDetailsCSV(items: FrameAndDoorDetail[]): string[][] {
    return tableCfgToCsv(tableCfg, items);
}

export function makeDoorDetails(items: PurchaseOrderItem[], hardwareDetail: HardwareDetailItem[] = []): FrameAndDoorDetail[] {
    const dr: FrameAndDoorDetail[] = selectMany(items.filter(v => v.kind === "door").map(i => {
        if(!i.openings || i.openings.length === 0) {
            return [{
                opening: -i.id,
                openingSequence: 0,
                openingLocationTransition: "",
                openingType: i.frameType,
                qty: i.qty,
                name: "",
                purchaseOrderItem: i,
                type: i.frameType,
                doorHardwarePreps: i.doorPreps,
                doorWoodDetail: i.doorWoodDetail,
                frameHardwarePreps: {},
            }] as FrameAndDoorDetail[];
        }

        return i.openings.map((op): FrameAndDoorDetail => {

            let name = op.name
            if(op.frameProfile === "COM") {
                name = `${op.name} (COM)`
            }
            if(op.type === "Pair") {
                name = `${op.name} (${op.subType})`
            }

            const hw = hardwareDetail.find(h=> h.openingId === op.id);
            const exactHeight = getExactHeight(op.nominalHeight, hw)

            return {
                opening: op.id,
                openingSequence: op.seqNumber,
                openingLocationTransition: op.locationTransition,
                openingType: op.type,
                openingSubType: op.subType || "",
                qty: op.qty,
                name: name,
                purchaseOrderItem: i,
                type: op.type,
                doorHardwarePreps: op.doorHardwarePreps,
                doorWoodDetail: i.doorWoodDetail,
                frameHardwarePreps: {},
                nominalHeight: op.nominalHeight,
                nominalWidth: op.nominalWidth,
                hardwarePrepDiagramName: hw?.prepDiagramName || "",
                doorExactHeight: exactHeight,
                frameExactHeight: "",
            }
        })
    }), v => v);

    orderByAscending(dr, v => v.openingSequence)

    return dr;
}

function getExactHeight(nominalHeight: string, hw?: HardwareDetailItem) {
    const fmt = isMetric(nominalHeight) ? MetricDistance.showMM : ImperialDistance.showInches
    if(!hw) {
        const ht = genericDistanceParser(nominalHeight).universalValue();
        return fmt(ht - ImperialDistance.parse("7/8\"").universalValue());
    }
    
    let exact = hw.manager.exactDoorHeight();
    return fmt(exact);
}

export function useDoorDetails(items: PurchaseOrderItem[], hardware: HardwareDetailItem[]): FrameAndDoorDetail[] {
    return useMemo(() => makeDoorDetails(items, hardware), [items, hardware]);
}